// Login.js
import React, { useState } from 'react';
import { Lock, Mail, Eye, EyeOff, AlertCircle, CheckCircle, Loader } from 'lucide-react';
import authService from './AuthService';
import arcEdLogo from '../assets/brand/ARC-Ed-Wordmark-Gold.svg';

const Login = ({ onLogin, onSwitchToRegister }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) setError('');
    if (message) setMessage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      const result = await authService.signIn(formData.email, formData.password);
      if (result.success) {
        setMessage(result.message);
        if (onLogin) onLogin(result.user);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!formData.email) {
      setError('Please enter your email address first.');
      return;
    }
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const result = await authService.resetPassword(formData.email);
      if (result.success) setMessage(result.message);
      else setError(result.error);
    } catch (err) {
      setError('Failed to send reset email. Please try again.');
      console.error('Password reset error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen mesh flex items-center justify-center p-4">
      <div className="surface-xl max-w-md w-full p-8 sm:p-10">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-5">
            <img
              src={arcEdLogo}
              alt="ARC Ed"
              className="h-16 sm:h-20 w-auto object-contain"
            />
          </div>
          <p className="eyebrow mb-3 text-arc-gold">Be DEFIant</p>
          <h1 className="headline text-3xl mb-2">
            Sign in to <span className="text-arc-gold">FIRE</span>Tracker
          </h1>
          <p className="text-arc-muted text-sm">What gets measured gets manifested.</p>
        </div>

        {/* Alert Messages */}
        {error && (
          <div className="mb-5 p-3 rounded-xl border border-arc-deep/40 bg-arc-deep/10 flex items-start gap-2.5">
            <AlertCircle className="text-arc-light flex-shrink-0 mt-0.5" size={18} />
            <span className="text-arc-light text-sm leading-relaxed">{error}</span>
          </div>
        )}

        {message && (
          <div className="mb-5 p-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 flex items-start gap-2.5">
            <CheckCircle className="text-emerald-400 flex-shrink-0 mt-0.5" size={18} />
            <span className="text-emerald-300 text-sm leading-relaxed">{message}</span>
          </div>
        )}

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Email */}
          <div>
            <label htmlFor="email" className="eyebrow block mb-2">
              Email
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-arc-dim" size={18} />
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@arcrypto.io"
                className="w-full pl-10 pr-4 py-3 rounded-xl text-sm"
                required
                disabled={loading}
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <label htmlFor="password" className="eyebrow block mb-2">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-arc-dim" size={18} />
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter your password"
                className="w-full pl-10 pr-12 py-3 rounded-xl text-sm"
                required
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-arc-dim hover:text-arc-ink transition-colors"
                disabled={loading}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {/* Forgot Password */}
          <div className="text-right">
            <button
              type="button"
              onClick={handleForgotPassword}
              className="text-xs font-mono uppercase tracking-[0.14em] text-arc-gold hover:text-arc-light transition-colors"
              disabled={loading}
            >
              Forgot password?
            </button>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="btn-tactile btn-gold w-full py-3 px-4 rounded-xl text-sm font-semibold flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader className="animate-spin mr-2" size={18} />
                Signing in…
              </>
            ) : (
              'Sign In'
            )}
          </button>
        </form>

        {/* Divider */}
        <div className="my-7 rule" />

        {/*
         * Membership-gated access. Self-signup was disabled as part of the
         * GHL paywall rollout — FIRE Tracker is now a Galaxy/Apex member
         * benefit, not a free signup. New users must purchase through GHL,
         * which provisions the Firebase account on the back end.
         *
         * `onSwitchToRegister` is intentionally NOT wired to a register
         * button anymore. The prop is still accepted for backward compat
         * but no longer reachable from the UI.
         */}
        <div className="text-center">
          {/*
           * Membership callout. Because email-enumeration protection is on,
           * Firebase returns the same generic credential error whether the
           * email is wrong OR has no account at all — so we can't show a
           * non-member a tailored "no account" message at the password step.
           * Instead we make the membership path prominent and always visible,
           * so anyone who lands here (or fails a sign-in) sees exactly where
           * to go: FIRE Tracker is a Galaxy/Apex member benefit.
           */}
          <div className="p-5 rounded-xl border border-arc-gold/30 bg-arc-gold/10 text-center">
            <p className="eyebrow text-arc-gold mb-1.5">New here?</p>
            <p className="text-sm text-arc-ink leading-relaxed mb-4">
              FIRE Tracker is a members-only tool. Access is included with an
              active <span className="text-arc-gold font-semibold">Galaxy</span> or{' '}
              <span className="text-arc-gold font-semibold">Apex</span> membership.
            </p>
            <a
              href="https://arcrypto.io/membership"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-tactile btn-gold inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold"
            >
              Become a member
            </a>
          </div>

          {/*
           * Legal links — Privacy + Terms.
           *
           * Required on any auth surface that collects credentials. By
           * showing these links above the fold on the sign-in form, a
           * user can review the policies before submitting a password,
           * which is the legally relevant moment.
           *
           * Links open in a new tab so the auth flow isn't interrupted.
           */}
          <div className="mt-6 pt-5 border-t border-arc-line flex items-center justify-center gap-3 text-[11px] text-arc-dim">
            <a
              href="https://arcrypto.io/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-arc-ink transition-colors underline-offset-4 hover:underline"
            >
              Privacy
            </a>
            <span aria-hidden="true">·</span>
            <a
              href="https://arcrypto.io/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-arc-ink transition-colors underline-offset-4 hover:underline"
            >
              Terms
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
